import trapFocus from '../../../scripts/modules/trap-focus';

/**
 * @class Drawer
 */
class Drawer {
    $element;
    id;
    previouslyFocused = null;
    isShown = false;

    constructor(element) {
        this.$element = element;
        this.id = this.$element.getAttribute('data-drawer') || this.$element.id;

        this.$element.setAttribute('aria-hidden', 'true');
        this.$element.setAttribute('aria-modal', 'true');
        this.$element.setAttribute('tabindex', '-1');

        document.addEventListener('click', this.handleTriggerClick, true);
    }

    /**
     * Show the drawer element, trap the current focus within it, listen for some
     * specific key presses and fire all registered callbacks for `show` event.
     *
     * @param {Event} event
     */
    show = (event) => {
        // If the drawer is already open, abort
        if (this.isShown) return this;

        // Keep a reference to the currently focused element
        // to be able to restore it later.
        this.previouslyFocused = document.activeElement;
        this.isShown = true;
        this.$element.removeAttribute('aria-hidden');

        // Set the focus to the drawer element.
        moveFocusToDrawer(this.$element);

        document.body.addEventListener('focus', this.handleOutsideFocus, true);
        this.$element.addEventListener('keydown', this.handleKeyDown, true);

        // Dispatch a `show` event
        this.dispatch('show', event);

        return this;
    };

    /**
     * Hide the drawer element, restore the focus to the previously
     * active element, stop listening for some specific key presses
     * and fire all registered callbacks for `hide` event.
     *
     * @param {Event} event
     */
    hide = (event) => {
        // If the drawer is already closed, abort
        if (!this.isShown) return this;

        this.isShown = false;

        this.$element.setAttribute('aria-hidden', 'true');

        // Set the focus to the previously focused element
        this.previouslyFocused
            ? this.previouslyFocused.focus()
            : document.body.focus();

        // Stop listening for the events we only care for while the drawer is shown.
        document.body.removeEventListener(
            'focus',
            this.handleOutsideFocus,
            true
        );
        this.$element.removeEventListener('keydown', this.handleKeyDown, true);

        // Dispatch a `hide` event
        this.dispatch('hide', event);

        return this;
    };

    /**
     * Destroy the current instance after making sure the drawer has been hidden
     * all associated listeners were removed from drawer openers and closers.
     */
    destroy = () => {
        // Hide the drawer to avoid destroying an open instance
        this.hide();

        // Remove the click event delegates for our openers and closers
        document.removeEventListener('click', this.handleTriggerClick, true);

        // Dispatch a `destroy` event
        this.dispatch('destroy');

        return this;
    };

    /**
     * Dispatch a custom event from the DOM element associated with this drawer.
     * This allows authors to listen for and respond to the events in their
     * own code.
     *
     * @param {String} eventName - The custom event name to dispatch.
     * @param {Event} event
     */
    dispatch = (eventName, event) => {
        this.$element.dispatchEvent(
            new CustomEvent(eventName, {
                detail: event,
                cancelable: true,
            })
        );
    };

    /**
     * Register a new callback for the given event.
     *
     * @param {String='show','hide','destroy'} eventName - The event to register the callback for.
     * @param {Function} callback - The callback function to execute when the given event is fired.
     * @param {(Object|Boolean)} [options]
     */
    on = (eventName, callback, options) => {
        this.$element.addEventListener(eventName, callback, options);
        return this;
    };

    /**
     * Unregister an existing callback for the given event.
     *
     * @param {String='show','hide','destroy'} eventName - The event to unregister the callback for.
     * @param {Function} callback
     * @param {(Object|Boolean)} [options]
     */
    off = (eventName, callback, options) => {
        this.$element.removeEventListener(eventName, callback, options);
        return this;
    };

    /**
     * Add a delegated event listener for when elements that open or close
     * the drawer are clicked, and call `show` or `hide`, respectively.
     *
     * @param {Event}
     */
    handleTriggerClick = (event) => {
        const target = event.target;
        if (target.matches(`[data-drawer-show="${this.id}"]`)) {
            this.show(event);
        }
        if (
            target.matches(`[data-drawer-hide="${this.id}"]`) ||
            (target.matches('[data-drawer-hide]') &&
                target.closest('[aria-modal="true"]') === this.$element)
        ) {
            this.hide(event);
        }
    };

    handleKeyDown = (event) => {
        // This is an escape hatch in case there are nested open drawers, so that
        // only the top most drawer gets interacted with
        if (
            document.activeElement?.closest('[aria-modal="true"]') !==
            this.$element
        ) {
            return;
        }

        // If the drawer is shown and the ESC key is pressed, prevent any further
        // effects from the ESC key and hide the drawer, unless its role is
        // `alertdialog`, which should be modal
        if (
            event.key === 'Escape' &&
            this.$element.getAttribute('role') !== 'alertdialog'
        ) {
            event.preventDefault();
            this.hide(event);
        }
        // If the drawer is shown and the TAB key is pressed, make sure the focus
        // stays trapped within the drawer element
        if (event.key === 'Tab') {
            trapFocus(this.$element, event);
        }
    };

    /**
     * Make sure the focus stays trapped inside the drawer.
     *
     * If anything outside the drawer (or another drawer in case of nested drawers)
     * receives focus while the drawer is shown, move focus back to the drawer.
     *
     * @see https://github.com/KittyGiraudel/a11y-drawer/issues/177
     *
     * @param {FocusEvent} event
     */
    handleOutsideFocus = (event) => {
        if (
            !event.target.closest(
                '[aria-modal="true"], [data-drawer-ignore-focus-trap]'
            )
        )
            moveFocusToDrawer(this.$element);
    };
}

/**
 * Set the focus to the first element with `autofocus` with the element
 * or the element itself.
 *
 * @param {HTMLElement} element
 */
function moveFocusToDrawer(element) {
    const focusedElement = element.querySelector('[autofocus]') || element;
    focusedElement.focus();
}

export default Drawer;
